import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ClipboardCheck,
  Sparkles,
  X,
  Send,
  Loader2,
  Wrench,
  Paperclip,
  FileText,
  Image as ImageIcon,
} from "lucide-react";
import { api, uploadFormData, type ApiResponse } from "@/api/client";
import i18n from "@/i18n";
import { useAgent } from "./AgentProvider";
import AgentMarkdown from "./AgentMarkdown";
import ProposalCard from "./ProposalCard";
import PlanPanel from "./PlanPanel";
import type {
  AgentEntityContext,
  AgentProposalSummary,
  AttachmentDescriptor,
  ChatTurnResult,
  WidgetMessage,
} from "./types";

// Route → on-screen entity. The widget passes this to /chat so the model starts grounded on
// what you're looking at (matches the server's optional `context` arg). Only detail routes
// with an :id segment qualify.
const ROUTE_ENTITY: Array<{ re: RegExp; type: string; label: (id: string) => string }> = [
  {
    re: /^\/orders\/([^/]+)$/,
    type: "order",
    label: (id) => i18n.t("shared.agent.entityOrder", { id: id.slice(-6) }),
  },
  {
    re: /^\/leads\/([^/]+)$/,
    type: "lead",
    label: (id) => i18n.t("shared.agent.entityLead", { id: id.slice(-6) }),
  },
  {
    re: /^\/customers\/([^/]+)$/,
    type: "customer",
    label: (id) => i18n.t("shared.agent.entityCustomer", { id: id.slice(-6) }),
  },
  {
    re: /^\/departures\/([^/]+)$/,
    type: "departure",
    label: (id) => i18n.t("shared.agent.entityDeparture", { id: id.slice(-6) }),
  },
];

function entityFromPath(pathname: string): AgentEntityContext | null {
  for (const r of ROUTE_ENTITY) {
    const m = pathname.match(r.re);
    if (m && m[1] !== "new") return { entityType: r.type, entityId: m[1], label: r.label(m[1]) };
  }
  return null;
}

let _mid = 0;
const nextId = () => `m${++_mid}`;

// Empty-state examples — show admins the breadth (it reads everything + prepares changes).
// Illustrative, not clickable: they reference placeholder entities ("Martin", "this order")
// the admin swaps for their real ones.
const ASK_EXAMPLES = [
  i18n.t("shared.agent.ask1"),
  i18n.t("shared.agent.ask2"),
  i18n.t("shared.agent.ask3"),
  i18n.t("shared.agent.ask4"),
  i18n.t("shared.agent.ask5"),
];
const DO_EXAMPLES = [
  i18n.t("shared.agent.do1"),
  i18n.t("shared.agent.do2"),
  i18n.t("shared.agent.do3"),
  i18n.t("shared.agent.do4"),
];

export default function MakejWidget() {
  const { t } = useTranslation();
  const { enabled, isOpen, open, close, pendingSeed, consumeSeed } = useAgent();
  const location = useLocation();
  const entity = useMemo(() => entityFromPath(location.pathname), [location.pathname]);
  const queryClient = useQueryClient();

  // Pending approvals across ALL conversations — proposals raised over EMAIL (or in a closed
  // chat) have no other UI surface. Shown pinned above the transcript + badge on the launcher.
  const { data: pendingData } = useQuery({
    queryKey: ["agent-pending-proposals"],
    queryFn: () =>
      api.get<ApiResponse<(AgentProposalSummary & { createdAt: string })[]>>(
        "/api/admin/agent/proposals",
      ),
    enabled,
    refetchInterval: 60_000,
  });
  const pending = pendingData?.data ?? [];

  const [messages, setMessages] = useState<WidgetMessage[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attached, setAttached] = useState<AttachmentDescriptor | null>(null);
  const [attaching, setAttaching] = useState(false);
  const conversationIdRef = useRef<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    setError(null);
    setAttaching(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await uploadFormData<ApiResponse<AttachmentDescriptor>>(
        "/api/admin/agent/attach",
        fd,
      );
      setAttached(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("shared.agent.attachFailed"));
    } finally {
      setAttaching(false);
    }
  };

  const send = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || busy) return;
    setError(null);
    setInput("");
    const sentAttachment = attached;
    setAttached(null);
    setMessages((prev) => [...prev, { id: nextId(), role: "user", text: trimmed }]);
    setBusy(true);
    try {
      const res = await api.post<ApiResponse<ChatTurnResult>>("/api/admin/agent/chat", {
        message: trimmed,
        conversationId: conversationIdRef.current ?? undefined,
        context: entity ? { entityType: entity.entityType, entityId: entity.entityId } : undefined,
        attachment: sentAttachment ?? undefined,
      });
      conversationIdRef.current = res.data.conversationId;
      setMessages((prev) => [
        ...prev,
        {
          id: nextId(),
          role: "assistant",
          text: res.data.reply,
          proposals: res.data.proposals,
          plan: res.data.plan,
        },
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("shared.agent.chatFailed"));
    } finally {
      setBusy(false);
    }
  };

  // Auto-send a seeded prompt from a contextual button (e.g. lead "Build offer"), once.
  useEffect(() => {
    if (isOpen && pendingSeed) {
      const seed = pendingSeed;
      consumeSeed();
      void send(seed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, pendingSeed]);

  // Keep the transcript pinned to the newest message.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  if (!enabled) return null;

  // ── Launcher (collapsed) ─────────────────────────────────────────────────────────
  if (!isOpen) {
    return (
      <button
        onClick={open}
        aria-label={t("shared.agent.openAssistant")}
        className="bg-primary-600 hover:bg-primary-700 shadow-primary-600/30 fixed bottom-5 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full text-white shadow-lg transition-transform hover:scale-105"
      >
        <Sparkles className="h-6 w-6" />
        {pending.length > 0 && (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1 text-[11px] font-bold text-white ring-2 ring-white">
            {pending.length}
          </span>
        )}
      </button>
    );
  }

  // ── Panel (expanded) ──────────────────────────────────────────────────────────────
  return (
    <div className="fixed bottom-5 right-5 z-50 flex h-[600px] max-h-[calc(100vh-2.5rem)] w-[400px] max-w-[calc(100vw-2.5rem)] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-primary-600 flex items-center justify-between px-4 py-3 text-white">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5" />
          <span className="font-semibold">MAKEJ!</span>
          {entity && (
            <span className="rounded-full bg-white/20 px-2 py-0.5 text-[11px] font-medium">
              {entity.label}
            </span>
          )}
        </div>
        <button
          onClick={close}
          aria-label={t("common.actions.close")}
          className="rounded p-1 hover:bg-white/20"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Transcript */}
      <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-3">
        {pending.length > 0 && (
          <div className="space-y-2 rounded-xl border border-amber-200 bg-amber-50/60 p-2.5 dark:border-amber-800 dark:bg-amber-900/10">
            <div className="flex items-center justify-between">
              <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                <ClipboardCheck className="h-3.5 w-3.5" />
                {t("shared.agent.waitingApproval", { count: pending.length })}
              </p>
              <button
                onClick={() =>
                  queryClient.invalidateQueries({ queryKey: ["agent-pending-proposals"] })
                }
                className="text-[11px] text-amber-600 hover:underline dark:text-amber-400"
              >
                {t("common.actions.refresh")}
              </button>
            </div>
            {pending.map((p) => (
              <ProposalCard key={p.id} proposal={p} />
            ))}
          </div>
        )}
        {messages.length === 0 && (
          <div className="mt-4 space-y-3 px-1">
            <div className="text-center">
              <Sparkles className="mx-auto mb-2 h-7 w-7 text-gray-400 opacity-40" />
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t("shared.agent.intro")}
              </p>
              {entity && (
                <p className="mt-1 text-xs text-gray-400">
                  {t("shared.agent.lookingAt")} <span className="font-medium">{entity.label}</span>.
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                {t("shared.agent.askHeading")}
              </p>
              <ul className="space-y-1 text-xs text-gray-500 dark:text-gray-400">
                {ASK_EXAMPLES.map((ex) => (
                  <li key={ex}>“{ex}”</li>
                ))}
              </ul>
            </div>
            <div className="space-y-1.5">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                {t("shared.agent.doHeading")}
              </p>
              <ul className="space-y-1 text-xs text-gray-500 dark:text-gray-400">
                {DO_EXAMPLES.map((ex) => (
                  <li key={ex}>“{ex}”</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {messages.map((m) =>
          m.role === "user" ? (
            <div key={m.id} className="flex justify-end">
              <div className="bg-primary-600 max-w-[85%] rounded-2xl rounded-br-sm px-3 py-2 text-sm text-white">
                {m.text}
              </div>
            </div>
          ) : (
            <div key={m.id} className="space-y-2">
              <div className="max-w-[92%] rounded-2xl rounded-bl-sm bg-gray-100 px-3 py-2 text-gray-900 dark:bg-gray-800 dark:text-gray-100">
                {m.text ? (
                  <AgentMarkdown>{m.text}</AgentMarkdown>
                ) : (
                  <p className="text-sm italic text-gray-400">{t("shared.agent.noReply")}</p>
                )}
              </div>
              {m.plan ? (
                <PlanPanel plan={m.plan} />
              ) : (
                m.proposals?.map((p) => <ProposalCard key={p.id} proposal={p} />)
              )}
            </div>
          ),
        )}

        {busy && (
          <div className="flex items-center gap-2 px-1 text-sm text-gray-400">
            <Wrench className="h-3.5 w-3.5 animate-pulse" />
            <span>{t("shared.agent.working")}</span>
          </div>
        )}
        {error && (
          <div className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-600 dark:bg-red-900/20">
            {error}
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="border-t border-gray-200 p-2.5 dark:border-gray-700">
        {/* Attached-file chip */}
        {(attached || attaching) && (
          <div className="mb-2 flex items-center gap-2 rounded-lg bg-gray-100 px-2.5 py-1.5 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-300">
            {attaching ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : attached?.kind === "image" ? (
              <ImageIcon className="h-3.5 w-3.5" />
            ) : (
              <FileText className="h-3.5 w-3.5" />
            )}
            <span className="truncate">
              {attaching ? t("shared.agent.uploading") : attached?.filename}
            </span>
            {attached && (
              <button
                onClick={() => setAttached(null)}
                aria-label={t("shared.agent.removeAttachment")}
                className="ml-auto rounded p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        )}
        <div className="flex items-end gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/*,application/pdf,text/csv,text/plain,.csv,.tsv,.txt"
            onChange={onPickFile}
            className="hidden"
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={busy || attaching}
            aria-label={t("shared.agent.attachTitle")}
            title={t("shared.agent.attachTitle")}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-300 text-gray-500 hover:bg-gray-100 disabled:opacity-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-800"
          >
            <Paperclip className="h-4 w-4" />
          </button>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void send(input);
              }
            }}
            rows={1}
            placeholder={t("shared.agent.inputPlaceholder")}
            className="focus:border-primary-500 focus:ring-primary-500/20 max-h-28 flex-1 resize-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          />
          <button
            onClick={() => void send(input)}
            disabled={busy || !input.trim()}
            aria-label={t("common.actions.send")}
            className="bg-primary-600 hover:bg-primary-700 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}
